/**
 * "Invite customer" (spec §6.3 / §7): creates the invitation only, then stays open in a "created"
 * state showing its status and offering Send / Resend (new link) and Copy link while it is pending
 * and unexpired (the same actions and eligibility rules as the panel, nothing automatic; a
 * terminal send DTO or reconciled row shows its status instead) and Done. The View is pure (node vitest); the container submits
 * the create and renders the page's row over the page's actions, owning neither.
 */
import { useEffect, useRef, useState, type Ref } from 'react';
import { Check, Copy, Send } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { NativeSelect } from '@/components/ui/native-select';
import { adminApi, type AdminInvitation, type InvitationRole } from '../services/adminApi';
import {
  INVITATION_ROLES,
  invitationRowActions,
  invitationStatusBadgeClass,
  invitationStatusLabel,
  mapCreateInvitationFailure,
  sendBlockMessage,
  usableAcceptUrl,
  type InvitationRowAction,
  type SendBlock,
} from './orgProvisioning';
import { useDialogEscape } from './useDialogEscape';
import { RECONCILE_FAILED_MESSAGE, formatSentCue, type InvitationActionsState, type SentCue } from './invitationActions';
import type { UseInvitationActions } from './useInvitationActions';

const LABEL_CLASS = 'mb-1 block text-sm font-semibold';
const ALERT_CLASS = 'rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive';

export interface InviteCustomerDialogViewProps {
  email: string;
  role: InvitationRole;
  /** For the created invitation's status and eligibility (expiry is also checked client-side). */
  now: Date;
  /** The create error before creation; the action error afterwards. */
  error: string | null;
  submitting: boolean;
  /** Set after a successful create; the dialog then shows the actions instead of the form. */
  created: AdminInvitation | null;
  sending: boolean;
  /**
   * The row is held by anything other than a send (a copy, the refresh after a failed send, or a
   * failed refresh): Send and Copy link are disabled and the fallback link is hidden.
   */
  linkBusy: boolean;
  /** The refresh after a failed send failed: "Could not refresh this invitation." with Retry. */
  refreshFailed: boolean;
  copied: boolean;
  /**
   * The CURRENT accept link for the read-only fallback after the clipboard refused; derived from
   * the invitation so a resend shows the new link. Rendered only while the row is unheld (not
   * sending, not linkBusy, not refreshFailed) and its link is usable.
   */
  copyFallbackUrl: string | null;
  sentCue: SentCue | null;
  /** A 429 cooldown or the 409 send limit for this invitation; Send is disabled while set. */
  sendBlock: SendBlock | null;
  onEmailChange: (email: string) => void;
  onRoleChange: (role: InvitationRole) => void;
  onSubmit: () => void;
  onSend: () => void;
  onCopyLink: () => void;
  onRetryRefresh: () => void;
  onDone: () => void;
  onClose: () => void;
  /**
   * Receives the control focus moves to once the invitation is created: Send / Resend while it can
   * be sent, otherwise Done.
   */
  createdFocusRef?: Ref<HTMLButtonElement>;
}

function sentNote(created: AdminInvitation, sentCue: SentCue | null): string {
  if (sentCue) return formatSentCue(sentCue);
  if (created.sendCount > 0) {
    return `Sent ${created.sendCount} time${created.sendCount === 1 ? '' : 's'}; only the latest link works.`;
  }
  return 'Nothing has been sent yet. Send it now, or copy the link onto a call.';
}

/** Shown instead of the sent note once the invitation is Accepted, Joined by domain, Revoked or Expired. */
function terminalNote(statusLabel: string): string {
  return statusLabel === 'Expired'
    ? 'This invitation has expired and can no longer be sent or copied. Use New invitation in the list to replace it.'
    : 'This invitation can no longer be sent or copied.';
}

/**
 * The read-only fallback shows the CURRENT link of the created invitation, so a resend shows the
 * new one, and nothing once the invitation is no longer pending and unexpired (its link is dead).
 */
export function copyFallbackUrlFor(
  created: AdminInvitation | null,
  copyFallbackInvitationId: string | null,
  now: Date,
): string | null {
  return created !== null && copyFallbackInvitationId === created.id ? usableAcceptUrl(created, now) : null;
}

export function InviteCustomerDialogView({
  email,
  role,
  now,
  error,
  submitting,
  created,
  sending,
  linkBusy,
  refreshFailed,
  copied,
  copyFallbackUrl,
  sentCue,
  sendBlock,
  onEmailChange,
  onRoleChange,
  onSubmit,
  onSend,
  onCopyLink,
  onRetryRefresh,
  onDone,
  onClose,
  createdFocusRef,
}: InviteCustomerDialogViewProps) {
  const canSubmit = !submitting && email.trim() !== '';
  const canDismiss = !submitting && !sending;
  const dismiss = created ? onDone : onClose;
  // The panel's eligibility rules (`invitationRowActions`): a send DTO or a reconciled row may be Accepted,
  // AutoResolved, Revoked or Expired, and then nothing can be sent or copied.
  const createdActions: InvitationRowAction[] = created ? invitationRowActions(created, now) : [];
  const canSend = createdActions.includes('send') || createdActions.includes('resend');
  const createdUrl = created ? usableAcceptUrl(created, now) : null;
  const createdStatus = created ? invitationStatusLabel(created, now) : '';
  // No link while the row is held or its refresh failed, because the link it
  // would show may be the one the server just rotated away; it reappears, current, once released.
  const rowHeld = sending || linkBusy || refreshFailed;
  const fallbackUrl = createdUrl !== null && !rowHeld ? copyFallbackUrl : null;
  return (
    <Modal open onOverlayClick={() => { if (canDismiss) dismiss(); }}>
      <Modal.Content maxWidth="lg" aria-labelledby="invite-customer-title">
        <Modal.Header className="px-6 py-4">
          <h2 id="invite-customer-title" className="text-lg font-medium text-foreground">Invite customer</h2>
        </Modal.Header>
        <Modal.Body className="px-6 py-4">
          {created ? (
            <div className="flex flex-col gap-4">
              <p role="status" className="text-sm text-foreground">
                Invitation created for <strong>{created.email}</strong> as {created.role}.
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Status</span>
                <Badge variant="outline" className={invitationStatusBadgeClass(createdStatus)}>{createdStatus}</Badge>
              </div>
              <div aria-live="polite" className="text-sm text-muted-foreground">
                {canSend ? sentNote(created, sentCue) : terminalNote(createdStatus)}
                {copied && createdUrl && <span className="ml-1">Link copied.</span>}
              </div>
              {(canSend || createdUrl) && (
                <div className="action-buttons">
                  {canSend && (
                    <Button type="button" variant="secondary" size="sm" disabled={rowHeld || sendBlock !== null} onClick={onSend} ref={createdFocusRef}>
                      <Send />
                      {sending ? 'Sending…' : createdActions.includes('resend') ? 'Resend (new link)' : 'Send'}
                    </Button>
                  )}
                  {createdUrl && (
                    <Button type="button" variant="secondary" size="sm" disabled={rowHeld} onClick={onCopyLink}>
                      {copied ? <Check /> : <Copy />}
                      {copied ? 'Copied' : 'Copy link'}
                    </Button>
                  )}
                </div>
              )}
              {canSend && sendBlock && (
                <div role="status" className="text-sm text-muted-foreground">{sendBlockMessage(sendBlock)}</div>
              )}
              {refreshFailed && (
                <div role="alert" className={`${ALERT_CLASS} flex items-center justify-between gap-2`}>
                  <span>{RECONCILE_FAILED_MESSAGE}</span>
                  <Button type="button" variant="ghost" size="sm" onClick={onRetryRefresh}>Retry</Button>
                </div>
              )}
              {fallbackUrl && (
                <Input
                  readOnly
                  value={fallbackUrl}
                  aria-label="Invitation link"
                  onFocus={(e) => e.currentTarget.select()}
                />
              )}
              {error && <div role="alert" className={ALERT_CLASS}>{error}</div>}
            </div>
          ) : (
            <form
              id="invite-customer-form"
              className="flex flex-col gap-4"
              onSubmit={(e) => { e.preventDefault(); if (canSubmit) onSubmit(); }}
            >
              <p className="text-sm text-muted-foreground">
                Nothing is sent until you press Send. You can also copy the link onto a call. The first
                non-staff Owner accepts the terms and establishes ownership.
              </p>

              <div>
                <label htmlFor="invite-customer-email" className={LABEL_CLASS}>Email</label>
                <Input
                  id="invite-customer-email"
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="ceo@company.example"
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="invite-customer-role" className={LABEL_CLASS}>Role</label>
                <NativeSelect
                  id="invite-customer-role"
                  value={role}
                  onChange={(e) => onRoleChange(e.target.value as InvitationRole)}
                >
                  {INVITATION_ROLES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </NativeSelect>
              </div>

              {error && <div role="alert" className={ALERT_CLASS}>{error}</div>}
            </form>
          )}
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-2 px-6 py-4">
          {created ? (
            <Button type="button" onClick={onDone} disabled={sending} ref={canSend ? undefined : createdFocusRef}>Done</Button>
          ) : (
            <>
              <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" form="invite-customer-form" disabled={!canSubmit}>
                {submitting ? 'Creating…' : 'Create invitation'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}

/**
 * The created state the dialog renders, from the page's list and the page's actions:
 * the page's row for the created invitation (the create's DTO only until the page's list holds
 * it), and that invitation's own hold, Retry, cue, block, error and fallback link, because the
 * controller is shared with the panel and may carry other rows' state.
 */
export function createdInvitationViewState(
  createdDto: AdminInvitation | null,
  invitations: AdminInvitation[],
  actions: InvitationActionsState,
  now: Date,
): Pick<
  InviteCustomerDialogViewProps,
  'created' | 'sending' | 'linkBusy' | 'refreshFailed' | 'copied' | 'copyFallbackUrl' | 'sentCue' | 'sendBlock'
> & { actionError: string | null } {
  if (createdDto === null) {
    return {
      created: null,
      sending: false,
      linkBusy: false,
      refreshFailed: false,
      copied: false,
      copyFallbackUrl: null,
      sentCue: null,
      sendBlock: null,
      actionError: null,
    };
  }
  const created = invitations.find((inv) => inv.id === createdDto.id) ?? createdDto;
  const pendingAction = actions.pending[created.id];
  return {
    created,
    sending: pendingAction === 'send',
    // Every other hold (copy, reconcile) disables the link actions.
    linkBusy: pendingAction !== undefined && pendingAction !== 'send',
    refreshFailed: created.id in actions.reconcileFailed,
    copied: actions.copiedInvitationId === created.id,
    copyFallbackUrl: copyFallbackUrlFor(created, actions.copyFallbackInvitationId, now),
    sentCue: actions.sentCue?.invitationId === created.id ? actions.sentCue : null,
    sendBlock: actions.sendBlocks[created.id] ?? null,
    actionError: actions.actionErrorInvitationId === created.id ? actions.actionError : null,
  };
}

export function InviteCustomerDialog({
  orgId,
  initialEmail = '',
  initialRole = 'Owner',
  invitations,
  actions,
  onInvitationCreated,
  onInvited,
  onClose,
}: {
  orgId: string;
  /** Prefilled when replacing an expired invitation from the panel. */
  initialEmail?: string;
  initialRole?: InvitationRole;
  /** The page's list; the created state renders the page's row for its invitation. */
  invitations: AdminInvitation[];
  /**
   * The page's invitation actions for this org (`useInvitationActions`, one controller per org): the
   * controller the panel renders too, so a hold, send key or cooldown started here outlives the
   * dialog and shows on the panel's row once it closes. The dialog calls no hook for it.
   */
  actions: UseInvitationActions;
  /**
   * Receives the DTO the create returned; the page upserts it into its list only while `orgId` is
   * still the page's org, and supersedes its reads still in flight (`applyInvitationUpdate`).
   */
  onInvitationCreated: (orgId: string, invitation: AdminInvitation) => void;
  /** Every way out once an invitation exists (Done, Escape, overlay): the parent closes and reloads the panel. */
  onInvited: () => void;
  /** The way out before anything was created. */
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [role, setRole] = useState<InvitationRole>(initialRole);
  const [createError, setCreateError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The create's DTO, shown only until the page's list holds the row (the same handler upserts it).
  const [createdDto, setCreatedDto] = useState<AdminInvitation | null>(null);
  const now = new Date();
  // The dialog makes no read and keeps no copy a read could leave stale: the page's
  // controller reconciles through the page's org-scoped, sequenced reader and applies every send
  // DTO to the page's list, and the created state is that row with its share of the page's actions.
  const view = createdInvitationViewState(createdDto, invitations, actions, now);
  const { created, sending } = view;
  // Once the invitation exists the form (and the field that had focus) is gone: move focus to
  // Send / Resend, or to Done when the invitation can no longer be sent.
  const createdFocusRef = useRef<HTMLButtonElement>(null);
  const createdId = created?.id ?? null;
  useEffect(() => {
    if (createdId !== null) createdFocusRef.current?.focus();
  }, [createdId]);
  // Done, Escape and the overlay once an invitation exists: close, and the parent reloads. Done
  // stays enabled during a reconcile, so a hung or failed refresh never traps the dialog, and
  // nothing is disposed: the controller is the page's, so whatever settles later
  // lands in the page's list for this org and the panel's row keeps the hold, key and cooldown.
  const dismiss = created ? onInvited : onClose;

  useDialogEscape(dismiss, !submitting && !sending);

  const handleSubmit = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setCreateError(null);
    try {
      const invitation = await adminApi.createInvitation(orgId, { email: trimmed, role });
      onInvitationCreated(orgId, invitation);
      setCreatedDto(invitation);
    } catch (err: unknown) {
      setCreateError(mapCreateInvitationFailure(err, trimmed));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <InviteCustomerDialogView
      email={email}
      role={role}
      now={now}
      error={created ? view.actionError : createError}
      submitting={submitting}
      created={created}
      sending={sending}
      linkBusy={view.linkBusy}
      refreshFailed={view.refreshFailed}
      copied={view.copied}
      copyFallbackUrl={view.copyFallbackUrl}
      sentCue={view.sentCue}
      sendBlock={view.sendBlock}
      onEmailChange={setEmail}
      onRoleChange={setRole}
      onSubmit={() => void handleSubmit()}
      onSend={() => { if (created) void actions.send(created); }}
      onCopyLink={() => { if (created) void actions.copyLink(created); }}
      onRetryRefresh={() => { if (created) void actions.retryReconcile(created.id); }}
      onDone={onInvited}
      onClose={onClose}
      createdFocusRef={createdFocusRef}
    />
  );
}
